import React, { useRef, useState, useEffect } from 'react';
import { Button, Modal, Container, Row, Col, Alert } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import type { CameraCapabilities } from 'html5-qrcode';
import {
  useAppContext,
  callNativeMobileApp,
  useTranslator,
  useToast,
} from '@front-activerse/common-components';
import { findCameraWithTorch } from '../helpers';
import { normalizeBarcodeMeal } from '../helpers/normalizers';
import Lottie from 'lottie-react';

interface ScanBarCodeContainerProps {
  scanBarcode: (ean: string) => Promise<unknown>;
  cancelScanBarcode: () => void;
  fromUserMeals?: boolean;
}

type ZoomFeature = ReturnType<CameraCapabilities['zoomFeature']>;

const ScanBarCodeContainer: React.FC<ScanBarCodeContainerProps> = ({
  scanBarcode,
  cancelScanBarcode,
  fromUserMeals,
}) => {
  const navigate = useNavigate();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const isAnalyzingRef = useRef(false);
  const zoomFeatureRef = useRef<ZoomFeature | null>(null);
  const readerElementId = 'barcode-reader';

  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [isTorchSupported, setIsTorchSupported] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [isSearchingCamera, setIsSearchingCamera] = useState(false);
  const [noFoodError, setNoFoodError] = useState(false);
  const [rateLimitExceeded, setRateLimitExceeded] = useState(false);
  const [productNotFoundError, setProductNotFoundError] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const { toastError, toastSuccess } = useToast();

  const { t } = useTranslator();

  const {
    lotties: { loaderStars },
    images: {
      closeWhiteIcon,
      flashOnIcon,
      flashOffIcon,
      infoWhiteIcon,
      trackerScanBarCodeTipsImg,
      videoPoster,
    },
  } = useAppContext();

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  // Toggle torch/flash
  const toggleTorch = async () => {
    if (!scannerRef.current || !isCameraActive) return;

    try {
      const track = await scannerRef.current.getRunningTrackCameraCapabilities();
      if (track.torchFeature && track.torchFeature().isSupported()) {
        await track.torchFeature().apply(!isTorchOn);
        setIsTorchOn(!isTorchOn);
      }
    } catch (err) {
      console.error('Error toggling torch:', err);
    }
  };

  // Handle successful barcode scan
  const handleScanSuccess = async (decodedText: string) => {
    console.log('weszlo do handleScanSuccess', decodedText);
    // Block subsequent calls while analyzing (using ref to avoid closure issues)
    if (isAnalyzingRef.current) {
      console.log('Already analyzing, ignoring scan');
      return;
    }

    isAnalyzingRef.current = true;
    setIsScanning(false);
    setNoFoodError(false);
    setRateLimitExceeded(false);
    setProductNotFoundError(false);
    setIsAnalyzing(true);
    try {
      const response: any = await scanBarcode(decodedText);

      if (response.__typename === 'AnalyzedDailyTrackerIngredients') {
        await stopScanner();

        sessionStorage.setItem('tracker_depth', '2');

        // calculate random id
        const dietMealId = Math.floor(Math.random() * 1000000000);

        const normalizedProps = normalizeBarcodeMeal({
          ...response,
          dietMealId,
        });

        if (fromUserMeals) {
          navigate(`/meals/user-meals/describe-meal/meal-details/${dietMealId}?isBarcode=true`, {
            state: {
              mealData: normalizedProps.mealData,
              requestId: response.requestId,
              timestamp: Date.now(),
            },
          });
        } else {
          navigate(`/tracker/describe-meal/meal-details/${dietMealId}?isBarcode=true`, {
            state: {
              mealData: normalizedProps.mealData,
              requestId: response.requestId,
              timestamp: Date.now(),
            },
          });
        }
      } else if (response.__typename === 'NotFoodError') {
        setNoFoodError(true);
      } else if (response.__typename === 'RateLimitExceeded') {
        setRateLimitExceeded(true);
      } else if (response.__typename === 'ProductNotFoundError') {
        setProductNotFoundError(true);
      } else {
        toastError();
      }
    } catch (error) {
      toastError();
    } finally {
      isAnalyzingRef.current = false;
      setIsAnalyzing(false);
    }
  };

  // Handle scan error (this fires continuously, so we don't log every frame)
  const handleScanError = (errorMessage: string) => {
    // Only log real errors
    const isExpectedError =
      errorMessage.includes('NotFoundException') ||
      errorMessage.includes('No barcode or QR code detected') ||
      errorMessage.includes('QR code parse error');

    if (!isExpectedError) {
      console.warn('Scan error:', errorMessage);
    }
  };

  // Start barcode scanner with best available camera (torch support preferred)
  const startScanner = async () => {
    try {
      setError(null);
      setIsScanning(true);
      setIsSearchingCamera(true);

      // Wait a bit to ensure DOM is ready
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Check if element exists
      const element = document.getElementById(readerElementId);
      if (!element) {
        throw new Error('Scanner element not found in DOM');
      }

      // Initialize Html5Qrcode if not already done
      if (!scannerRef.current) {
        try {
          scannerRef.current = new Html5Qrcode(readerElementId);
          console.log('Html5Qrcode initialized');
        } catch (initError) {
          console.error('Error initializing Html5Qrcode:', initError);
          throw initError;
        }
      }

      // Check if scanner is already running
      try {
        const state = await scannerRef.current.getState();
        console.log('Current scanner state:', state);

        // State 2 = SCANNING, if already scanning, don't start again
        if (state === 2) {
          console.log('Scanner already running, skipping start');
          setIsCameraActive(true);
          setIsScanning(false);
          setIsSearchingCamera(false);
          return;
        }

        // State 1 = NOT_STARTED or transitioning, wait a bit
        if (state === 1) {
          console.log('Scanner in transition, waiting...');
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } catch (stateError) {
        console.log('Could not get scanner state (might be first run):', stateError);
      }

      let cameraIdToUse = selectedCameraId;

      // If we don't have a selected camera yet, find the best one
      if (!cameraIdToUse) {
        console.log('No camera selected yet, finding best camera...');

        // First, start with default camera to request permissions
        // This will trigger the iOS permission prompt
        try {
          const betterCameraId = await findCameraWithTorch();
          if (betterCameraId) {
            cameraIdToUse = betterCameraId;
            setSelectedCameraId(cameraIdToUse);
          } else {
            // No camera with torch found, use the initial one
            const initialConstraints: MediaStreamConstraints = {
              video: {
                facingMode: 'environment',
                width: { ideal: 1920 },
                height: { ideal: 1080 },
              },
            };

            const initialStream = await navigator.mediaDevices.getUserMedia(initialConstraints);
            const initialTrack = initialStream.getVideoTracks()[0];
            const initialDeviceId = initialTrack.getSettings().deviceId;

            cameraIdToUse = initialDeviceId || '';
            setSelectedCameraId(cameraIdToUse);
          }
        } catch (permissionErr) {
          console.error('Permission denied or camera not available:', permissionErr);
          throw permissionErr;
        }
      }

      if (!cameraIdToUse) {
        throw new Error('No camera available');
      }

      console.log(`Starting scanner with camera: ${cameraIdToUse}`);

      // Configure scanner for barcodes and QR codes
      const config = {
        fps: 10,
        qrbox: 250,
        formatsToSupport: [
          9, // EAN_13
          10, // EAN_8
        ],
      };

      // Start scanning with selected camera
      console.log('Starting scanner...');
      await scannerRef.current.start(cameraIdToUse, config, handleScanSuccess, handleScanError);

      // hack to work properly on iOS
      await new Promise((resolve) => setTimeout(resolve, 0));

      // Fix for iOS: Move video element out of library's control
      // The library's container forces 0x0 dimensions on the video element
      const videoParentElement = document.getElementById('barcode-reader');
      const videoElement = document.querySelector('#barcode-reader video') as HTMLVideoElement;
      if (videoElement) {
        videoElement.setAttribute('playsInline', 'true');
        videoElement.setAttribute('playsinline', 'true');
        videoElement.setAttribute('poster', videoPoster);
        videoElement.setAttribute('style', 'opacity: 0;');
        videoElement.playsInline = true;
        videoElement.poster = videoPoster;

        // Remove video from library container
        const originalParent = videoElement.parentElement;
        if (originalParent) {
          originalParent.removeChild(videoElement);
        }

        // Append directly to body (outside library's control)
        videoParentElement?.appendChild(videoElement);

        // Remove width/height attributes and set proper styles
        videoElement.removeAttribute('width');
        videoElement.removeAttribute('height');

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        videoElement.setAttribute(
          'style',
          `
          display: block !important;
          visibility: visible !important;
          position: fixed !important;
          top: 0px !important;
          left: 0px !important;
          width: ${viewportWidth}px !important;
          height: ${viewportHeight}px !important;
          object-fit: cover !important;
          z-index: 1 !important;
          transition: opacity 1s ease-in-out !important;
          opacity: 0;
        `
        );

        setTimeout(() => {
          videoElement.style.setProperty('opacity', '1');
        }, 100);
      }

      setIsCameraActive(true);
      setIsScanning(false);
      setIsSearchingCamera(false);

      // Check torch support
      try {
        const track = await scannerRef.current.getRunningTrackCameraCapabilities();
        const torchSupported = track.torchFeature && track.torchFeature().isSupported();
        setIsTorchSupported(!!torchSupported);
        console.log('Torch supported:', torchSupported);

        const zoomFeature = track.zoomFeature ? track.zoomFeature() : null;
        if (zoomFeature && zoomFeature.isSupported()) {
          zoomFeatureRef.current = zoomFeature;
          const min = zoomFeature.min();
          const max = zoomFeature.max();
          const currentValue = zoomFeature.value();
          const normalizedValue = typeof currentValue === 'number' ? currentValue : min;
          const clampedDefaultZoom = Math.min(Math.max(2, min), max);
          const nextZoomValue = Number.isFinite(clampedDefaultZoom) ? clampedDefaultZoom : min;

          if (nextZoomValue !== normalizedValue) {
            try {
              await zoomFeature.apply(nextZoomValue);
            } catch (zoomErr) {
              console.error('Error setting default zoom level:', zoomErr);
            }
          }
        } else {
          zoomFeatureRef.current = null;
        }
      } catch (err) {
        console.log('Torch not supported:', err);
        setIsTorchSupported(false);
        zoomFeatureRef.current = null;
      }

      console.log('Scanner started successfully');
    } catch (err) {
      console.error('Error starting scanner (full error):', err);
      console.error('Error type:', typeof err);
      console.error('Error details:', JSON.stringify(err, null, 2));

      let errorMessage = 'Unknown error occurred';

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err && typeof err === 'object') {
        const errObj = err as { message?: string; error?: string };
        errorMessage = errObj.message || errObj.error || JSON.stringify(err);
      }

      console.error('Final error message:', errorMessage);
      setError(`Unable to start scanner: ${errorMessage}`);
      setIsCameraActive(false);
      setIsScanning(false);
      setIsSearchingCamera(false);
    }
  };

  // Stop scanner
  const stopScanner = async () => {
    try {
      // First, turn off torch if it's on
      if (isTorchOn && scannerRef.current) {
        try {
          const track = await scannerRef.current.getRunningTrackCameraCapabilities();
          if (track.torchFeature && track.torchFeature().isSupported()) {
            await track.torchFeature().apply(false);
            console.log('Torch turned off before stopping scanner');
          }
        } catch (torchErr) {
          console.error('Error turning off torch:', torchErr);
        }
      }

      if (scannerRef.current) {
        try {
          const state = await scannerRef.current.getState();
          console.log('Scanner state before stop:', state);

          // Only stop if scanner is actually running (state 2 = SCANNING)
          if (state === 2) {
            await scannerRef.current.stop();
            console.log('Scanner stopped successfully');
          } else {
            console.log('Scanner not running, no need to stop');
          }

          // Clear the scanner instance
          scannerRef.current.clear();
        } catch (err) {
          console.error('Error stopping scanner:', err);
          // Try to force clear even if there was an error
          try {
            scannerRef.current.clear();
          } catch (clearErr) {
            console.error('Error clearing scanner:', clearErr);
          }
        }
      }

      setIsCameraActive(false);
      setIsTorchOn(false);
      zoomFeatureRef.current = null;
    } catch (err) {
      console.error('Error in stopScanner:', err);
    }
  };

  // Start scanner on mount
  useEffect(() => {
    let mounted = true;
    let cleanupCalled = false;

    const initScanner = async () => {
      // Small delay to avoid race conditions with React Strict Mode
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (mounted && !cleanupCalled) {
        console.log('Initializing scanner from useEffect');
        await startScanner();
      } else {
        console.log('Component unmounted before scanner could start');
      }
    };

    initScanner();

    // Stop camera on navigation back or page unload
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      console.log('handleBeforeUnload called');
      stopScanner();
    };

    const handlePageHide = (event: PageTransitionEvent) => {
      console.log('handlePageHide called');
      stopScanner();
    };

    // Handle browser back/forward navigation (popstate)
    const handlePopState = () => {
      console.log('handlePopState called - user navigated back/forward');
      if (isCameraActive) {
        stopScanner();
      } else {
        if (window.location.pathname === '/tracker/scan-barcode') {
          startScanner();
        } else {
          stopScanner();
        }
      }
    };

    const handleMessage = (e: MessageEvent) => {
      if (e.data.action === 'release_camera_and_close_modal') {
        handleCloseClick();
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('message', handleMessage);

    return () => {
      console.log('useEffect cleanup called');
      mounted = false;
      cleanupCalled = true;
      stopScanner();

      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseClick = async () => {
    // Stop scanner and wait for it to fully stop
    await stopScanner();

    // Give a small delay to ensure camera is fully released
    await new Promise((resolve) => setTimeout(resolve, 100));

    callNativeMobileApp({
      action: 'close_modal',
    });
    navigate(-1);
  };

  const handleProductNotFoundClick = () => {
    setProductNotFoundError(false);
    toastSuccess(t('tracker.scanFood.analyze.productReportedConfirmation.content'));
  };

  return (
    <>
      <div className="scan-plate">
        {/* Barcode reader container */}
        <div id={readerElementId} className="barcode-reader-container" />

        {/* Overlay with controls */}
        <div
          className={
            isAnalyzing
              ? 'camera-overlay-fullscreen-barcode-lottie'
              : 'camera-overlay-fullscreen-barcode'
          }
        >
          {/* Scan area indicator */}
          {isCameraActive && !isAnalyzing && <div className="scan-area-barcode" />}

          {isAnalyzing && (
            <div className="scan-area-barcode-lottie">
              <Lottie animationData={loaderStars} loop={true} />
              <p>{t('tracker.describeFood.loader.calculating')}</p>
            </div>
          )}

          {/* Top bar with controls */}
          <div className="top-controls">
            <button className="scan-plate-button" onClick={handleCloseClick}>
              <img src={closeWhiteIcon} alt="" />
            </button>
            <button className="scan-plate-button" onClick={toggleModal}>
              <img src={infoWhiteIcon} alt="" />
            </button>
          </div>

          {/* Bottom controls */}
          {!isAnalyzing && (
            <div
              className={
                noFoodError || rateLimitExceeded || productNotFoundError || error
                  ? 'bottom-controls justify-content-center'
                  : 'bottom-controls'
              }
            >
              {noFoodError || rateLimitExceeded || productNotFoundError || error ? (
                <>
                  {noFoodError && (
                    <Alert color="warning" className="mb-0" fade={false}>
                      <p className="mb-0 text-text-main-color text-start">
                        {t('tracker.describeFood.analyze.noFood.notification')}
                      </p>
                    </Alert>
                  )}

                  {rateLimitExceeded && (
                    <Alert color="danger" className="mb-0" fade={false}>
                      <p className="mb-0 text-text-main-color text-start">
                        {t('tracker.limitUsageError.notification')}
                      </p>
                    </Alert>
                  )}

                  {productNotFoundError && (
                    <Alert
                      color="warning"
                      className="mb-0"
                      style={{ marginTop: '-60px' }}
                      fade={false}
                    >
                      <p className="text-text-main-color text-start">
                        {t('tracker.scanFood.analyze.productNotFound.notification')}
                      </p>

                      <div className="text-center">
                        <Button color="link" size="sm" onClick={handleProductNotFoundClick}>
                          <span className="semi-bold">
                            {t('tracker.scanFood.analyze.productNotFound.notification.button')}
                          </span>
                        </Button>
                      </div>
                    </Alert>
                  )}

                  {error && (
                    <div className="text-center">
                      <Alert color="danger" className="mb-0" fade={false}>
                        <p className="mb-0 text-text-main-color text-start">
                          {t('tracker.scanFood.noAccessCamera.notification')}
                        </p>
                      </Alert>
                      {/* Restart button if not active */}
                      {!isCameraActive && !isSearchingCamera && (
                        <Button
                          color="link"
                          size="sm"
                          onClick={() => window.location.reload()}
                          disabled={isSearchingCamera || isScanning}
                        >
                          <p>{t('tracker.scan.button.refreshCamera')}</p>
                        </Button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Torch button */}
                  {isCameraActive && isTorchSupported ? (
                    <button
                      className="scan-plate-button"
                      onClick={toggleTorch}
                      disabled={isScanning}
                    >
                      <img src={isTorchOn ? flashOnIcon : flashOffIcon} alt="" />
                    </button>
                  ) : (
                    <button className="scan-plate-button" disabled>
                      <img src={flashOffIcon} alt="" />
                    </button>
                  )}

                  {!isCameraActive && isSearchingCamera && (
                    <p>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {t('tracker.scan.findingCamera')}
                    </p>
                  )}

                  {isCameraActive && !isSearchingCamera && (
                    <p>{t('tracker.scan.barcodeDescription')}</p>
                  )}

                  <div style={{ width: '40px', height: '40px' }} />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal
        className="from-bottom"
        isOpen={showModal}
        fullscreen={true}
        size="lg"
        toggle={toggleModal}
      >
        <div className="select-insert-date">
          <Container>
            <Row>
              <Col xs="12" md={{ size: 4, offset: 4 }}>
                <img className="img-fluid" src={trackerScanBarCodeTipsImg} alt="" />
                <h2 className="title-medium bold">{t('tracker.tipsModal.title')}</h2>
                <p className="text-text-variant-color mb-xl">
                  {t('tracker.tipsModal.description.barcode')}
                </p>

                <Button color="primary" className="w-100" onClick={toggleModal}>
                  {t('tracker.tipsModal.button')}
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Modal>
    </>
  );
};

export default ScanBarCodeContainer;
